/** Selection and annotations in viewport CSS pixels. */

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface MosaicTile {
  x: number;
  y: number;
  color: string;
}

export type ArrowEnd = 'start' | 'end';

export type BoxAnnotation = Extract<Annotation, { kind: 'rect' | 'ellipse' }>;

export type Handle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export type Annotation =
  | {
      kind: 'rect';
      x: number;
      y: number;
      width: number;
      height: number;
      color: string;
      stroke: number;
    }
  | {
      kind: 'ellipse';
      x: number;
      y: number;
      width: number;
      height: number;
      color: string;
      stroke: number;
    }
  | {
      kind: 'arrow';
      /** Straight shaft from the drag start to the tip, WeChat/QQ style. */
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      color: string;
      stroke: number;
    }
  | {
      kind: 'brush';
      points: Point[];
      color: string;
      stroke: number;
    }
  | {
      kind: 'mosaic';
      /** Tile side in CSS pixels, twice the toolbar stroke. */
      size: number;
      tiles: MosaicTile[];
    }
  | {
      kind: 'text';
      x: number;
      y: number;
      text: string;
      color: string;
      fontSize: number;
      /**
       * Column width in CSS pixels, captured when the text is first committed.
       * Later drags and edits reuse it instead of measuring to the selection edge.
       */
      wrapWidth: number;
    };

const MIN_SELECTION = 4;

export function normalizeRect(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): Rect {
  const x = Math.min(x0, x1);
  const y = Math.min(y0, y1);
  return {
    x,
    y,
    width: Math.abs(x1 - x0),
    height: Math.abs(y1 - y0),
  };
}

export function isUsableSelection(rect: Rect): boolean {
  return rect.width >= MIN_SELECTION && rect.height >= MIN_SELECTION;
}

export function containsPoint(rect: Rect, point: Point): boolean {
  return (
    point.x >= rect.x &&
    point.y >= rect.y &&
    point.x <= rect.x + rect.width &&
    point.y <= rect.y + rect.height
  );
}

/** Dragging the selection moves annotations with it. Resizing does not. */
export function translateAnnotations(
  items: readonly Annotation[],
  dx: number,
  dy: number,
): Annotation[] {
  return items.map((item) => translateAnnotation(item, dx, dy));
}

function translateAnnotation(
  item: Annotation,
  dx: number,
  dy: number,
): Annotation {
  switch (item.kind) {
    case 'rect':
    case 'ellipse':
    case 'text':
      return { ...item, x: item.x + dx, y: item.y + dy };
    case 'arrow':
      return {
        ...item,
        x1: item.x1 + dx,
        y1: item.y1 + dy,
        x2: item.x2 + dx,
        y2: item.y2 + dy,
      };
    case 'brush':
      return {
        ...item,
        points: item.points.map((point) => ({
          x: point.x + dx,
          y: point.y + dy,
        })),
      };
    case 'mosaic':
      return {
        ...item,
        tiles: item.tiles.map((tile) => ({
          ...tile,
          x: tile.x + dx,
          y: tile.y + dy,
        })),
      };
    default: {
      const unreachable: never = item;
      return unreachable;
    }
  }
}

const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

export function handlePoint(rect: Rect, handle: Handle): Point {
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;
  const midX = rect.x + rect.width / 2;
  const midY = rect.y + rect.height / 2;
  switch (handle) {
    case 'nw':
      return { x: rect.x, y: rect.y };
    case 'n':
      return { x: midX, y: rect.y };
    case 'ne':
      return { x: right, y: rect.y };
    case 'e':
      return { x: right, y: midY };
    case 'se':
      return { x: right, y: bottom };
    case 's':
      return { x: midX, y: bottom };
    case 'sw':
      return { x: rect.x, y: bottom };
    case 'w':
      return { x: rect.x, y: midY };
    default: {
      const unreachable: never = handle;
      return unreachable;
    }
  }
}

export function hitHandle(
  rect: Rect,
  point: Point,
  radius: number,
): Handle | null {
  for (const handle of HANDLES) {
    const at = handlePoint(rect, handle);
    if (
      Math.abs(point.x - at.x) <= radius &&
      Math.abs(point.y - at.y) <= radius
    ) {
      return handle;
    }
  }
  return null;
}

/** Resize by dragging one handle. The opposite corner stays put. */
export function resizeSelection(
  rect: Rect,
  handle: Handle,
  point: Point,
): Rect {
  let x0 = rect.x;
  let y0 = rect.y;
  let x1 = rect.x + rect.width;
  let y1 = rect.y + rect.height;
  if (handle.includes('w')) {
    x0 = point.x;
  }
  if (handle.includes('e')) {
    x1 = point.x;
  }
  if (handle.includes('n')) {
    y0 = point.y;
  }
  if (handle.includes('s')) {
    y1 = point.y;
  }
  return normalizeRect(x0, y0, x1, y1);
}

export function moveSelection(rect: Rect, dx: number, dy: number): Rect {
  return { ...rect, x: rect.x + dx, y: rect.y + dy };
}

/** Tile side is twice the toolbar stroke, matching the spacing between tiles. */
export function mosaicTileSize(stroke: number): number {
  return stroke * 2;
}

/**
 * Walk from the last tile toward `point`, dropping a tile every `tileSize`.
 * A leftover longer than half a tile gets one more tile on `point`.
 */
export function extendMosaicTiles(
  tiles: readonly MosaicTile[],
  point: Point,
  tileSize: number,
  sample: (point: Point) => string | null,
): MosaicTile[] {
  const next = tiles.map((tile) => ({ ...tile }));
  const last = next[next.length - 1];
  if (!last) {
    const color = sample(point);
    return color ? [{ x: point.x, y: point.y, color }] : next;
  }
  let cursor = last;
  let dx = point.x - cursor.x;
  let dy = point.y - cursor.y;
  let length = Math.hypot(dx, dy);
  if (!(length > tileSize) || !(tileSize > 0)) {
    if (length > tileSize / 2) {
      const color = sample(point);
      if (color) {
        next.push({ x: point.x, y: point.y, color });
      }
    }
    return next;
  }
  const stepX = (dx / length) * tileSize;
  const stepY = (dy / length) * tileSize;
  while (length > tileSize) {
    const at = { x: cursor.x + stepX, y: cursor.y + stepY };
    const color = sample(at);
    if (!color) {
      return next;
    }
    cursor = { x: at.x, y: at.y, color };
    next.push(cursor);
    length -= tileSize;
    dx = point.x - cursor.x;
    dy = point.y - cursor.y;
  }
  if (length > tileSize / 2) {
    const color = sample(point);
    if (color) {
      next.push({ x: point.x, y: point.y, color });
    }
  }
  return next;
}

export function resizeBoxAnnotation(
  item: BoxAnnotation,
  handle: Handle,
  point: Point,
): BoxAnnotation {
  return { ...item, ...resizeSelection(item, handle, point) };
}

export function hitArrowEnd(
  item: Extract<Annotation, { kind: 'arrow' }>,
  point: Point,
  radius: number,
): ArrowEnd | null {
  if (Math.hypot(point.x - item.x2, point.y - item.y2) <= radius) {
    return 'end';
  }
  if (Math.hypot(point.x - item.x1, point.y - item.y1) <= radius) {
    return 'start';
  }
  return null;
}

export function placeArrowEnd(
  item: Extract<Annotation, { kind: 'arrow' }>,
  end: ArrowEnd,
  point: Point,
): Extract<Annotation, { kind: 'arrow' }> {
  if (end === 'start') {
    return { ...item, x1: point.x, y1: point.y };
  }
  return { ...item, x2: point.x, y2: point.y };
}

/** Movement past this counts as a drag. A shorter press edits text. */
export const ANNOTATION_DRAG_THRESHOLD = 4;

/** Extra hit padding around an arrow or brush, in CSS pixels. */
export const STROKE_HIT_PADDING = 8;

/** Fullwidth glyph used to reserve one character of width while placing text. */
export const TEXT_REFERENCE_GLYPH = '国';

const ARROW_WING = Math.PI / 6;

export function textFont(fontSize: number): string {
  return `${fontSize}px sans-serif`;
}

export function pointerMovedEnough(origin: Point, point: Point): boolean {
  return (
    Math.hypot(point.x - origin.x, point.y - origin.y) >=
    ANNOTATION_DRAG_THRESHOLD
  );
}

export function moveAnnotationAt(
  items: readonly Annotation[],
  index: number,
  dx: number,
  dy: number,
): Annotation[] {
  return items.map((item, itemIndex) =>
    itemIndex === index ? translateAnnotation(item, dx, dy) : item,
  );
}

/**
 * Keep a text origin inside the selection, leaving one glyph of width and one
 * line of height when the selection is large enough. A smaller selection only
 * keeps the origin on the selection rectangle.
 */
export function clampTextOrigin(
  origin: Point,
  selection: Rect,
  charWidth: number,
  lineHeight: number,
): Point {
  const right = selection.x + selection.width;
  const bottom = selection.y + selection.height;
  const maxX =
    charWidth > 0 && selection.width >= charWidth ? right - charWidth : right;
  const maxY =
    lineHeight > 0 && selection.height >= lineHeight
      ? bottom - lineHeight
      : bottom;
  return {
    x: clamp(origin.x, selection.x, Math.max(selection.x, maxX)),
    y: clamp(origin.y, selection.y, Math.max(selection.y, maxY)),
  };
}

/**
 * Break `text` on newline characters, then on `maxWidth`. A glyph wider than
 * the column stays on its own line so the selection clip can hide the overflow.
 * Non-positive width keeps each paragraph on one line; the caller clips it.
 */
export function wrapText(
  text: string,
  maxWidth: number,
  measure: (text: string) => number,
): string[] {
  if (text.length === 0) {
    return [];
  }
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    if (paragraph.length === 0 || !(maxWidth > 0)) {
      lines.push(paragraph);
      continue;
    }
    let current = '';
    for (const char of paragraph) {
      const next = current + char;
      if (current.length > 0 && measure(next) > maxWidth) {
        lines.push(current);
        current = char;
      } else {
        current = next;
      }
    }
    if (current.length > 0) {
      lines.push(current);
    }
  }
  return lines;
}

/**
 * Column width for wrapping. A committed annotation keeps {@link Annotation}
 * `wrapWidth`. Text that is still being placed for the first time uses the
 * distance from its origin to the selection's right edge.
 */
export function textColumnWidth(
  item: { x: number; wrapWidth?: number },
  selection: Rect | null,
): number {
  if (typeof item.wrapWidth === 'number') {
    return item.wrapWidth;
  }
  if (!selection) {
    return 0;
  }
  return Math.max(0, selection.x + selection.width - item.x);
}

export function visibleTextBoxes(
  item: Extract<Annotation, { kind: 'text' }>,
  selection: Rect | null,
  measure: (text: string) => number,
): Rect[] {
  const maxWidth = textColumnWidth(item, selection);
  const lines = wrapText(item.text, maxWidth, measure);
  const boxes: Rect[] = [];
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? '';
    const measured = line.length === 0 ? 0 : measure(line);
    if (measured <= 0) {
      continue;
    }
    boxes.push({
      x: item.x,
      y: item.y + index * item.fontSize,
      width: measured,
      height: item.fontSize,
    });
  }
  return boxes;
}

export interface ArrowParts {
  tip: Point;
  left: Point;
  right: Point;
}

export function arrowParts(
  item: Extract<Annotation, { kind: 'arrow' }>,
): ArrowParts | null {
  const dx = item.x2 - item.x1;
  const dy = item.y2 - item.y1;
  const length = Math.hypot(dx, dy);
  if (length < 1) {
    return null;
  }
  const angle = Math.atan2(dy, dx);
  const headLength = Math.min(Math.max(item.stroke * 4.5, 12), length * 0.65);
  return {
    tip: { x: item.x2, y: item.y2 },
    left: {
      x: item.x2 - headLength * Math.cos(angle - ARROW_WING),
      y: item.y2 - headLength * Math.sin(angle - ARROW_WING),
    },
    right: {
      x: item.x2 - headLength * Math.cos(angle + ARROW_WING),
      y: item.y2 - headLength * Math.sin(angle + ARROW_WING),
    },
  };
}

/**
 * Topmost annotation under `point`, or null. A mark stays hittable after it
 * leaves the selection. Rectangles and ellipses hit the stroke, not the
 * interior. Arrows include the head within {@link STROKE_HIT_PADDING};
 * brushes add half the stroke to that padding. Mosaic tiles are not hittable.
 */
export function hitAnnotationIndex(
  annotations: readonly Annotation[],
  point: Point,
  selection: Rect,
  measure: (text: string, fontSize: number) => number,
): number | null {
  for (let index = annotations.length - 1; index >= 0; index -= 1) {
    const item = annotations[index];
    if (item && annotationHits(item, point, selection, measure)) {
      return index;
    }
  }
  return null;
}

function annotationHits(
  item: Annotation,
  point: Point,
  selection: Rect,
  measure: (text: string, fontSize: number) => number,
): boolean {
  switch (item.kind) {
    case 'rect':
      return outlineHits(item, point, strokeSlop(item.stroke));
    case 'ellipse':
      return ellipseHits(item, point);
    case 'text':
      return visibleTextBoxes(item, selection, (text) =>
        measure(text, item.fontSize),
      ).some((box) => containsPoint(box, point));
    case 'arrow':
      return arrowHits(item, point);
    case 'brush':
      return brushHits(item, point);
    case 'mosaic':
      return false;
    default: {
      const unreachable: never = item;
      return unreachable;
    }
  }
}

function strokeSlop(stroke: number): number {
  return stroke / 2 + STROKE_HIT_PADDING;
}

function outlineHits(rect: Rect, point: Point, slop: number): boolean {
  const left = rect.x;
  const right = rect.x + rect.width;
  const top = rect.y;
  const bottom = rect.y + rect.height;
  const insideX = point.x >= left && point.x <= right;
  const insideY = point.y >= top && point.y <= bottom;
  if (insideX && insideY) {
    const inset = Math.min(
      point.x - left,
      right - point.x,
      point.y - top,
      bottom - point.y,
    );
    return inset <= slop;
  }
  const nearest = {
    x: clamp(point.x, left, right),
    y: clamp(point.y, top, bottom),
  };
  return Math.hypot(point.x - nearest.x, point.y - nearest.y) <= slop;
}

function ellipseHits(
  item: Extract<Annotation, { kind: 'ellipse' }>,
  point: Point,
): boolean {
  const rx = item.width / 2;
  const ry = item.height / 2;
  if (!(rx > 0) || !(ry > 0)) {
    return false;
  }
  const dx = point.x - (item.x + rx);
  const dy = point.y - (item.y + ry);
  const angle = Math.atan2(dy, dx);
  const edge = Math.hypot(rx * Math.cos(angle), ry * Math.sin(angle));
  return Math.abs(Math.hypot(dx, dy) - edge) <= strokeSlop(item.stroke);
}

function arrowHits(
  item: Extract<Annotation, { kind: 'arrow' }>,
  point: Point,
): boolean {
  const parts = arrowParts(item);
  if (!parts) {
    return false;
  }
  const shaftStart = { x: item.x1, y: item.y1 };
  const shaftEnd = { x: item.x2, y: item.y2 };
  if (
    strokeHits(point, shaftStart, shaftEnd, STROKE_HIT_PADDING) ||
    strokeHits(point, parts.tip, parts.left, STROKE_HIT_PADDING) ||
    strokeHits(point, parts.tip, parts.right, STROKE_HIT_PADDING) ||
    strokeHits(point, parts.left, parts.right, STROKE_HIT_PADDING)
  ) {
    return true;
  }
  return pointInTriangle(point, parts.tip, parts.left, parts.right);
}

function brushHits(
  item: Extract<Annotation, { kind: 'brush' }>,
  point: Point,
): boolean {
  const slop = item.stroke / 2 + STROKE_HIT_PADDING;
  const first = item.points[0];
  if (!first) {
    return false;
  }
  if (item.points.length === 1) {
    return Math.hypot(point.x - first.x, point.y - first.y) <= slop;
  }
  for (let index = 1; index < item.points.length; index += 1) {
    const previous = item.points[index - 1];
    const next = item.points[index];
    if (previous && next && strokeHits(point, previous, next, slop)) {
      return true;
    }
  }
  return false;
}

function strokeHits(
  point: Point,
  start: Point,
  end: Point,
  slop: number,
): boolean {
  const closest = closestOnSegment(point, start, end);
  return Math.hypot(point.x - closest.x, point.y - closest.y) <= slop;
}

function closestOnSegment(point: Point, start: Point, end: Point): Point {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return { x: start.x, y: start.y };
  }
  const t = clamp(
    ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared,
    0,
    1,
  );
  return { x: start.x + t * dx, y: start.y + t * dy };
}

function pointInTriangle(point: Point, a: Point, b: Point, c: Point): boolean {
  const sign = (p1: Point, p2: Point, p3: Point): number =>
    (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
  const d1 = sign(point, a, b);
  const d2 = sign(point, b, c);
  const d3 = sign(point, c, a);
  const hasNegative = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPositive = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNegative && hasPositive);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
